import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Grow } from '@framework-controls/core';
import { JSXIf } from '@framework-controls/jsxconditions';
import { IconVehicle, IconCalendar, IconProfile2 } from 'src/content/icons';
import { strings } from 'src/localization';
import * as css from './MenuSlide.module.scss';
import { Appointment, Advisor, ApptCust, ApptVeh, UpdateApptCustInp, UpdateApptVehInp, VehicleStockPhoto, PhotoList, DepartmentSpec, Customer, Transportation, VehicleRec, ascFormEngagementInput, ascMenuInteractionInput, ascCtaInteractionInput, SpecData } from 'src/models';
import MenuCalendar from './MenuCalendar/MenuCalendar';
import MenuProfile from './MenuProfile/MenuProfile';
import MenuSlideBtn from './MenuSlideBtn/MenuSlideBtn';
import MenuVehicle from './MenuVehicle/MenuVehicle';
import { IView } from 'Framework';
import { ascCtaInteraction, ascFormEngagement, ascMenuInteraction, getElementColor } from 'src/helpers/EventHelper/EventHelper';
import { IAppointmentState } from 'src/store/states';

interface IProps {
    openFlag: boolean;
    cartFlag: boolean;
    appointment?: Appointment;
    apptCust?: ApptCust;
    apptVeh?: ApptVeh;
    advisors?: Advisor[];
    vehiclePhoto?: VehicleStockPhoto;
    vehPhotoList?: PhotoList;
    theme: string;
    allowLogin: boolean;
    custEmailUpdate: boolean;
    deptSpecs: DepartmentSpec;
    specProfile: string;
    store: string;
    branch: string;
    errMsg: string;
    retCode: string;
    customerInfo: Customer;
    previewFlag: string;
    changeCustReturned: boolean;
    locale: string;
    transIndex?: Transportation;
    profileOpen: boolean;
    newVehicleMileage: string,
    prevMileage: number,
    identityVerified: boolean,
    mfaEnabled: boolean,
    employeeFlag?: boolean,
    mainPaneViewOrder: IView[];
    vehicleList: VehicleRec[];
    selectedVehicle?: string;
    desktopOrMobile: string;
    Itype: string | undefined;
    appointmentState: IAppointmentState;
    specData?: SpecData;
    sessionId: string;
    lexusMCLaunched: string;
    toyotaMCLaunched: string;
    lexusTDMCLaunched: string;
    toyotaTDMCLaunched: string;
    cartHandler: (event: any) => void;
    pushToMain?: (inputView: IView) => void;
    onApptCustUpdate?: (apptCust: UpdateApptCustInp) => void;
    updateApptVehicle?: (inp: UpdateApptVehInp) => void;
    getPhoto?: (year: string, make: string, model: string, id: string) => void;
    clearError: () => void;
    changeCustInfo: (cust: Customer, profileNum, store: string, branch: string, previewFlag: string) => void;
    setChangeCustReturned: (value: boolean) => void;
    onCustUpdate: (cust: Customer) => void;
    checkUniqueEmail: (email: string, nameId: string, profileNum: string, store: string, branch: string,
        firstName: string, lastName: string, phone: string) => void;
    setProfileOpen: (profileOpen: boolean) => void;
    setMenuOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

const MenuSlide = (props: IProps) => {
    const [profileOpenFlag, setProfileOpenFlag] = useState<boolean>(false);
    const [vehicleOpenFlag, setVehicleOpenFlag] = useState<boolean>(false);
    const [calendarOpenFlag, setCalendarOpenFlag] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement>(document.createElement('div'));
    const transformOriginValue = props.desktopOrMobile === 'M' ? 'bottom top' : 'left center';

    // If the profile button has been clicked, then invert its value
    // also make sure any other menu popout is closed.
    const profileHandler = useCallback((event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        if (props.employeeFlag) {
            return;
        }
        setProfileOpenFlag(profileOpenFlag => !profileOpenFlag);
        props.setProfileOpen(true);
        setVehicleOpenFlag(false);
        setCalendarOpenFlag(false);
        setAnchorEl(event.currentTarget);
        if (!profileOpenFlag) {
            const ascFormEngagementInp: ascFormEngagementInput = {
                pageType: 'contact',
                commType: 'form',
                commStatus: 'engage',
                elementText: strings.Profile,
                department: 'service',
                formName: 'Service_Portal_2',
                formType: 'service_appointment',
            };
            ascFormEngagement(ascFormEngagementInp, props.vehicleList, props.selectedVehicle);
        }
        if (props.cartFlag) {
            props.cartHandler(event.currentTarget);
        }
        const ascMenuInteractionInp : ascMenuInteractionInput = {
            pageType: 'service',
            elementText: strings.Profile,
            elementSubtype: 'cta_icon',
            elementType: 'menu',
            eventAction: 'click',
        };
        ascMenuInteraction(ascMenuInteractionInp);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.Profile,
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'other_buttons',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, event, props.vehicleList, props.selectedVehicle);
    }, [props.cartFlag, props.cartHandler, props.setProfileOpen, props.employeeFlag,
        profileOpenFlag, props.vehicleList, props.selectedVehicle]);

    const closeProfileHandler = useCallback((e: any) => {
        setProfileOpenFlag(false);
        props.setProfileOpen(false);
        const elementColor = getElementColor(e, 'color');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.Close,
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicleList, props.selectedVehicle);
    }, [props.selectedVehicle, props.setProfileOpen, props.vehicleList]);

    // If the vehicle button has been clicked, then invert its value
    // also make sure any other menu popout is closed.
    const vehicleHandler = useCallback((event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        setProfileOpenFlag(false);
        setVehicleOpenFlag(vehicleOpenFlag => !vehicleOpenFlag);
        setCalendarOpenFlag(false);
        setAnchorEl(event.currentTarget);
        if (props.cartFlag) {
            props.cartHandler(event.currentTarget);
        }
        const ascMenuInteractionInp : ascMenuInteractionInput = {
            pageType: 'service',
            elementText: strings.Vehicle,
            elementSubtype: 'cta_icon',
            elementType: 'menu',
            eventAction: 'click',
        };
        ascMenuInteraction(ascMenuInteractionInp);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.Vehicle,
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'other_buttons',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, event, props.vehicleList, props.selectedVehicle);
    }, [props.cartFlag, props.cartHandler, props.selectedVehicle, props.vehicleList]);

    const closeVehicleHandler = useCallback((e: any) => {
        setVehicleOpenFlag(false);
        const elementColor = getElementColor(e, 'color');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.Close,
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicleList, props.selectedVehicle);
    }, [props.vehicleList, props.selectedVehicle]);

    // If the calendar button has been clicked, then invert its value
    // also make sure any other menu popout is closed.
    const calendarHandler = useCallback((event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        setProfileOpenFlag(false);
        setVehicleOpenFlag(false);
        setCalendarOpenFlag(calendarOpenFlag => !calendarOpenFlag);
        setAnchorEl(event.currentTarget);
        if (props.cartFlag) {
            props.cartHandler(event.currentTarget);
        }
        const ascMenuInteractionInp : ascMenuInteractionInput = {
            pageType: 'service',
            elementText: strings.Calendar,
            elementSubtype: 'cta_icon',
            elementType: 'menu',
            eventAction: 'click',
        };
        ascMenuInteraction(ascMenuInteractionInp);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.Calendar,
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'other_buttons',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, event, props.vehicleList, props.selectedVehicle);
    }, [props.cartFlag, props.cartHandler, props.selectedVehicle, props.vehicleList]);

    const calendarCloseHandler = useCallback((e: any) => {
        setCalendarOpenFlag(false);
        const elementColor = getElementColor(e, 'color');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.Close,
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicleList, props.selectedVehicle);
    }, [props.vehicleList, props.selectedVehicle]);
    // if the menu slide is closed, then close all the menu popouts
    useEffect(() => {
        if (!props.openFlag) {
            setCalendarOpenFlag(false);
            setProfileOpenFlag(false);
            setVehicleOpenFlag(false);
        }
        if (props.cartFlag) {
            setCalendarOpenFlag(false);
            setProfileOpenFlag(false);
            setVehicleOpenFlag(false);
        }
    }, [props.openFlag, props.cartFlag]);

    const showProfileRef = useRef<HTMLDivElement>(null);
    // only build the Profile button if we have customer data

    const showProfile = (props?.apptCust && props.apptCust.custFirstName !== '') || props.customerInfo.login ?
        <div ref={showProfileRef} className={css.menuSlideBtn}>
            <MenuSlideBtn
                id='profileBtn'
                lit={strings.Profile}
                icon={<IconProfile2 style={{ height: '2.5rem' }} />}
                openFlag={profileOpenFlag}
                click={profileHandler}
                toolTip={strings.ProfileTitle}
                theme={props.theme}
            />
        </div> : null;

    const showVehRef = useRef<HTMLDivElement>(null);
    // only build the Vehicle button if we have a selected vehicle
    const showVeh = props?.apptVeh && props.apptVeh.make !== '' ?
        <div ref={showVehRef} className={css.menuSlideBtn}>
            <MenuSlideBtn
                id='vehicleBtn'
                lit={strings.Vehicle}
                icon={<IconVehicle style={{ height: '2.5rem' }} />}
                openFlag={vehicleOpenFlag}
                click={vehicleHandler}
                toolTip={strings.UpdateVehTip}
                theme={props.theme}
            />
        </div>:
        null;

    const showCalRef = useRef<HTMLDivElement>(null);
    // only build the calendar button if we have a selected date
    const showCal = props?.appointment && props.appointment.date !== '' ?
        <div ref={showCalRef} className={css.menuSlideBtn}>
            <MenuSlideBtn
                id='calendarBtn'
                lit={strings.Calendar}
                icon={<IconCalendar style={{ margin: '2px auto 0px auto', height: '2.3rem' }} />}
                openFlag={calendarOpenFlag}
                click={calendarHandler}
                toolTip={strings.UpdateApptTip}
                theme={props.theme}
            />
        </div>:
        null;

    const menuCal = props.advisors && props.appointment ?
        <MenuCalendar
            openFlag={calendarOpenFlag}
            close={calendarCloseHandler}
            appointment={props.appointment}
            advisors={props.advisors}
            anchorEl={anchorEl}
            theme={props.theme}
            showCalRef={showCalRef}
            locale={props.locale}
            transIndex={props.transIndex}
        /> :
        null;

    const menuProf = props.apptCust && props.onApptCustUpdate && props.pushToMain ?
        <MenuProfile
            openFlag={profileOpenFlag}
            close={closeProfileHandler}
            apptCust={props.apptCust}
            pushToMainPanes={props.pushToMain}
            onApptCustUpdate={props.onApptCustUpdate}
            anchorEl={anchorEl}
            theme={props.theme}
            showProfileRef={showProfileRef}
            allowLogin={props.allowLogin}
            custEmailUpdate={props.custEmailUpdate}
            clearError={props.clearError}
            changeCustInfo={props.changeCustInfo}
            setChangeCustReturned={props.setChangeCustReturned}
            checkUniqueEmail={props.checkUniqueEmail}
            onCustUpdate={props.onCustUpdate}
            specProfile={props.specProfile}
            branch={props.branch}
            store={props.store}
            errMsg={props.errMsg}
            retCode={props.retCode}
            customerInfo={props.customerInfo}
            previewFlag={props.previewFlag}
            changeCustReturned={props.changeCustReturned}
            profileOpen={props.profileOpen}
            setProfileopen={props.setProfileOpen}
            identityVerified={props.identityVerified}
            mfaEnabled={props.mfaEnabled}
            vehicleList={props.vehicleList}
            selectedVehicle={props.selectedVehicle}
            setMenuOpen={props.setMenuOpen}
            desktopOrMobile={props.desktopOrMobile}
        /> :
        null;

    const menuVeh = props.apptVeh && props.updateApptVehicle ?
        <MenuVehicle
            openFlag={vehicleOpenFlag}
            close={closeVehicleHandler}
            apptVeh={props.apptVeh}
            updateApptVehicle={props.updateApptVehicle}
            anchorEl={anchorEl}
            theme={props.theme}
            showVehRef={showVehRef}
            newVehicleMileage={props.newVehicleMileage}
            prevMileage={props.prevMileage}
            Itype={props.Itype}
            specData={props.specData}
            sessionId = {props.sessionId}
            lexusMCLaunched = {props.lexusMCLaunched}
            lexusTDMCLaunched = {props.lexusTDMCLaunched}
            toyotaMCLaunched = {props.toyotaMCLaunched}
            toyotaTDMCLaunched = {props.toyotaTDMCLaunched}
            appointment = {props.appointmentState}
        /> :
        null;

    return (
        <div style={{ height: '0px' }}>
            <Grow
                style={{ transformOrigin: transformOriginValue, display: 'flex' }}
                onHover={props.openFlag}>
                <div className={css.menuSlideBtnArea}>
                    {showProfile}
                    {showVeh}
                    {showCal}
                </div>
            </Grow>
            <JSXIf Condition={profileOpenFlag}>
                {menuProf}
            </JSXIf>
            <JSXIf Condition={vehicleOpenFlag}>
                {menuVeh}
            </JSXIf>
            <JSXIf Condition={calendarOpenFlag}>
                {menuCal}
            </JSXIf>
        </div>
    );
};

export default MenuSlide;